import { Fragment, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { Placeholder } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { COMMON_CURRENCY_PRECISION } from 'components/constants';
import i18nContext from 'components/i18n-context';
import { formatMoney } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import Button from 'uikit/Button/Button';
import './CounterpartiesTabTableRow.scss';

const CounterpartiesTabTableRow = ({ isLoading, counterpartyData, handleDownloadDocument }) => {
  const i18n = useContext(i18nContext);

  const getCounterpartyInfoBlock = (
    counterpartyBlockInfo,
    isDocument,
    isTransactionId,
    isAccountNumberSortCodeBic,
    isCounterpartyId
  ) => {
    if (isLoading) {
      return (
        <Placeholder as={'p'} animation={'glow'}>
          <Placeholder className={'counterparties-tab-loader-placeholder'} xs={12} bg={'secondary'} />
        </Placeholder>
      );
    } else if (isCounterpartyId) {
      return (
        <NavLink
          className={'counterparty-transaction-id-text'}
          to={`${ROUTE_PATHS.TRANSACTIONS}?search_text=${counterpartyBlockInfo}`}
        >
          {counterpartyBlockInfo}
        </NavLink>
      );
    } else if (isAccountNumberSortCodeBic) {
      return (
        <>
          {counterpartyBlockInfo.counterpartyAccountNumber && (
            <p>
              <b>{i18n.getMessage('counterparties.tab.table.rowContent.accountNumber')} </b>
              <p>
                {counterpartyBlockInfo?.counterpartyAccountNumber?.map((item, index) => {
                  if (item) {
                    return (
                      <Fragment key={index}>
                        {item}
                        {index < counterpartyBlockInfo.length - 1 && <br />}
                      </Fragment>
                    );
                  }
                })}
              </p>
            </p>
          )}
          {counterpartyBlockInfo.counterpartySortCode && (
            <p>
              <b>{i18n.getMessage('counterparties.tab.table.rowContent.sortCode')} </b>
              <p>
                {counterpartyBlockInfo?.counterpartySortCode?.map((item, index) => {
                  if (item) {
                    return (
                      <Fragment key={index}>
                        {item}
                        {index < counterpartyBlockInfo.length - 1 && <br />}
                      </Fragment>
                    );
                  }
                })}
              </p>
            </p>
          )}
          {counterpartyBlockInfo.counterpartyBic && (
            <p>
              <b>{i18n.getMessage('counterparties.tab.table.rowContent.bic')} </b>
              <p>
                {counterpartyBlockInfo?.counterpartyBic?.map((item, index) => {
                  if (item) {
                    return (
                      <Fragment key={index}>
                        {item}
                        {index < counterpartyBlockInfo.length - 1 && <br />}
                      </Fragment>
                    );
                  }
                })}
              </p>
            </p>
          )}
        </>
      );
    } else if (isDocument && counterpartyBlockInfo && typeof counterpartyBlockInfo === 'object') {
      return (
        <div className={'counterparty-tab-documents-wrapper'}>
          {Object.entries(counterpartyBlockInfo).map(([docKey, docs]) =>
            docs.map((document, index) => (
              <div key={`${docKey}_${document.id}_${index}`}>
                <Button
                  className={'counterparty-tab-link-button counterparty-tab-table-cell-link'}
                  onClick={() => handleDownloadDocument(document.id)}
                  isDisabled={false}
                >
                  {document.name}
                </Button>
                {docKey && (
                  <div className={'counterparty-tab-document'}>
                    <b>{i18n.getMessage('counterparties.tab.table.row.documents.transactionId')}</b>
                    <NavLink
                      className={'counterparty-transaction-id-text'}
                      to={`${ROUTE_PATHS.TRANSACTIONS}/${docKey}`}
                    >
                      {docKey}
                    </NavLink>
                  </div>
                )}
                {document.category && (
                  <div className={'counterparty-tab-document'}>
                    <b>{i18n.getMessage('counterparties.tab.table.row.documents.category')}</b> {document.category}
                  </div>
                )}
                {document.note && (
                  <div className={'counterparty-tab-document'}>
                    <b>{i18n.getMessage('counterparties.tab.table.row.documents.note')}</b> {document.note}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      );
    } else if (Array.isArray(counterpartyBlockInfo) && isTransactionId) {
      return (
        <p>
          {counterpartyBlockInfo.map((item, index) => {
            if (item) {
              return (
                <Fragment key={index}>
                  <NavLink className={'counterparty-transaction-id-text'} to={`${ROUTE_PATHS.TRANSACTIONS}/${item}`}>
                    {item}
                  </NavLink>
                  {index < counterpartyBlockInfo.length - 1 && <br />}
                </Fragment>
              );
            }
          })}
        </p>
      );
    } else if (Array.isArray(counterpartyBlockInfo)) {
      return (
        <p>
          {counterpartyBlockInfo.map((item, index) => {
            if (item) {
              return (
                <Fragment key={index}>
                  {item}
                  {index < counterpartyBlockInfo.length - 1 && <br />}
                </Fragment>
              );
            }
          })}
        </p>
      );
    } else if (typeof counterpartyBlockInfo === 'object' && counterpartyBlockInfo !== null) {
      return (
        <div>
          {Object.entries(counterpartyBlockInfo).map(([docKey, docs]) => (
            <div key={docKey}>
              <b>{docKey}:</b>
              <ul>
                {docs.map((doc) => (
                  <li key={doc.id}>
                    {doc.name} ({doc.size}b)
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    } else {
      return <p>{counterpartyBlockInfo}</p>;
    }
  };

  return (
    <tr>
      <td className={'counterparties-tab-table-cell'}>
        {getCounterpartyInfoBlock(counterpartyData.counterpartyNumber, false, false, false, true)}
      </td>
      <td className={'counterparties-tab-table-cell'}>{getCounterpartyInfoBlock(counterpartyData.direction)}</td>
      <td className={'counterparties-tab-table-cell'}>{getCounterpartyInfoBlock(counterpartyData.counterpartyName)}</td>
      <td className={'counterparties-tab-table-cell'}>
        {getCounterpartyInfoBlock(counterpartyData, false, false, true)}
      </td>
      <td className={'counterparties-tab-table-cell'}>
        {getCounterpartyInfoBlock(formatMoney(counterpartyData.totalAmountDefault, COMMON_CURRENCY_PRECISION))}
      </td>
      <td className={'counterparties-tab-table-cell'}>{getCounterpartyInfoBlock(counterpartyData.currencyDefault)}</td>
      <td className={'counterparties-tab-table-cell'}>
        {getCounterpartyInfoBlock(counterpartyData.transactionsCount)}
      </td>
      <td className={'counterparties-tab-table-cell'}>
        {getCounterpartyInfoBlock(counterpartyData.transactionNumbers, false, true)}
      </td>
      <td className={'counterparties-tab-table-cell counterparties-tab-table-document-cell'}>
        {getCounterpartyInfoBlock(counterpartyData.documents, true)}
      </td>
    </tr>
  );
};

CounterpartiesTabTableRow.propTypes = {
  isLoading: PropTypes.bool,
  counterpartyData: PropTypes.shape({
    counterpartyNumber: PropTypes.string,
    direction: PropTypes.string,
    counterpartyName: PropTypes.arrayOf(PropTypes.string),
    registrationNumber: PropTypes.arrayOf(PropTypes.string),
    counterpartyAccountNumber: PropTypes.arrayOf(PropTypes.string),
    counterpartySortCode: PropTypes.arrayOf(PropTypes.string),
    counterpartyBic: PropTypes.arrayOf(PropTypes.string),
    totalAmountDefault: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currencyDefault: PropTypes.string,
    transactionsCount: PropTypes.number,
    transactionNumbers: PropTypes.arrayOf(PropTypes.string),
    documents: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          size: PropTypes.number,
          contentType: PropTypes.string,
          accountNumber: PropTypes.string,
          transactionNumber: PropTypes.string,
          partyName: PropTypes.string,
          hidden: PropTypes.bool,
          category: PropTypes.string,
          note: PropTypes.string
        })
      )
    )
  }),
  handleDownloadDocument: PropTypes.func
};

export default CounterpartiesTabTableRow;
